import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const DentistNearBenbrook = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Where is the best dentist near Benbrook TX?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental is located just minutes from Benbrook at 4620 Citylake Blvd W, Fort Worth, TX 76132. With over 35 years of experience and a board-certified implant dentist, we're the top choice for Benbrook residents."
        }
      },
      {
        "@type": "Question",
        "name": "Does Fort Worth Dental accept patients from Benbrook?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We welcome patients from Benbrook and all surrounding areas. We're conveniently located just 10 minutes from downtown Benbrook with easy access via I-20."
        }
      },
      {
        "@type": "Question",
        "name": "What dental services are available near Benbrook?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental offers comprehensive dental services near Benbrook including dental implants, Invisalign, LANAP laser gum treatment, emergency dentistry, cosmetic dentistry, and family dental care."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Dentist Near Benbrook TX | Best Dental Care | Fort Worth Dental</title>
        <meta name="description" content="Looking for a dentist near Benbrook TX? Fort Worth Dental is just 10 minutes away. Board-certified implant dentist, LANAP laser treatment, Invisalign. Call (817) 985-3337." />
        <meta name="keywords" content="dentist near benbrook tx, benbrook dentist, dental implants benbrook, dentist near me benbrook, best dentist benbrook texas" />
        <link rel="canonical" href="https://fortworthdental.com/dentist-near-benbrook" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-teal-200 font-semibold mb-2">Serving Benbrook & Surrounding Areas</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Best Dentist Near Benbrook, TX</h1>
            <p className="text-xl mb-8">Fort Worth Dental is conveniently located just 10 minutes from Benbrook. Experience award-winning dental care with our expert team.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:8179853337" className="bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100">Call (817) 985-3337</a>
              <Link to="/contact-us" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-white/10">Schedule Appointment</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Location Info */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Why Benbrook Residents Choose Fort Worth Dental</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-3 rounded-full"><span className="text-2xl">🏆</span></div>
                  <div>
                    <h3 className="font-bold text-gray-800">Board-Certified Implant Dentist</h3>
                    <p className="text-gray-600">Dr. Mund is a Diplomate of the American Board of Oral Implantology - one of the few in the DFW area.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-3 rounded-full"><span className="text-2xl">⚡</span></div>
                  <div>
                    <h3 className="font-bold text-gray-800">LANAP Laser Gum Treatment</h3>
                    <p className="text-gray-600">Advanced laser gum disease treatment not available at most Benbrook-area dental offices.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-3 rounded-full"><span className="text-2xl">😊</span></div>
                  <div>
                    <h3 className="font-bold text-gray-800">35+ Years of Excellence</h3>
                    <p className="text-gray-600">Trusted by thousands of families in Benbrook and Fort Worth since 1990.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-3 rounded-full"><span className="text-2xl">🚗</span></div>
                  <div>
                    <h3 className="font-bold text-gray-800">Easy Access from Benbrook</h3>
                    <p className="text-gray-600">Just 10 minutes via I-20 or Dirks Road. Free parking available.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m28!1m12!1m3!1d53728.03844953!2d-97.48!3d32.68!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m13!3e6!4m5!1s0x864e6d3e!2sBenbrook%2C%20TX!3m2!1d32.6732!2d-97.4606!4m5!1s0x864e!2s4620%20Citylake%20Blvd%20W%2C%20Fort%20Worth%2C%20TX%2076132!3m2!1d32.7284!2d-97.4383!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Directions from Benbrook to Fort Worth Dental"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Dental Services for Benbrook Patients</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Dental Implants', desc: 'Permanent tooth replacement by a board-certified implant specialist', link: '/dental-implants' },
              { title: 'Invisalign', desc: 'Clear aligners for straighter teeth without metal braces', link: '/invisalign' },
              { title: 'LANAP Laser Treatment', desc: 'FDA-cleared laser gum disease treatment', link: '/lanap' },
              { title: 'Emergency Dentistry', desc: 'Same-day appointments for dental emergencies', link: '/emergency-dentist' },
              { title: 'Cosmetic Dentistry', desc: 'Veneers, whitening, and smile makeovers', link: '/cosmetic-dentistry' },
              { title: 'Family Dentistry', desc: 'Comprehensive care for the whole family', link: '/preventative-dentistry' },
            ].map((service, i) => (
              <Link key={i} to={service.link} className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition">
                <h3 className="font-bold text-gray-800 mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Where is the best dentist near Benbrook TX?</h3>
              <p className="text-gray-600">Fort Worth Dental is located just minutes from Benbrook at 4620 Citylake Blvd W, Fort Worth, TX 76132. With over 35 years of experience and a board-certified implant dentist, we're the top choice for Benbrook residents.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">Does Fort Worth Dental accept patients from Benbrook?</h3>
              <p className="text-gray-600">Yes! We welcome patients from Benbrook and all surrounding areas. We're conveniently located just 10 minutes from downtown Benbrook with easy access via I-20.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-2">What dental services are available near Benbrook?</h3>
              <p className="text-gray-600">Fort Worth Dental offers comprehensive dental services near Benbrook including dental implants, Invisalign, LANAP laser gum treatment, emergency dentistry, cosmetic dentistry, and family dental care.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience the Best Dental Care Near Benbrook?</h2>
          <p className="text-xl mb-8">Schedule your appointment today and see why patients drive from Benbrook to Fort Worth Dental.</p>
          <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 inline-block">Call (817) 985-3337</a>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Schedule Your Appointment</h2>
          <AppointmentForm source="benbrook_landing" />
        </div>
      </section>
    </div>
  );
};

export default DentistNearBenbrook;